/* eslint-disable semi */
import { createDecorator } from 'vue-class-component';
import * as utils from './utils';
import DecoratorConfigs from './DecoratorConfigs';

const debug = process.env.NODE_ENV === 'development';

let DECORATOR_NAME = '';

/**
 * そのコンポーネントがローディング変数を管理することを宣言します
 *
 * 下記のパラメータを指定できます。
 * label: ローディング変数名を指定します。
 * resetBefore: 実行前にローディング変数の初期化(FALSE)を行う処理を指定します。
 * activeOn: 処理の開始から終了までの間、ローディング変数をTRUEに設定すべき処理を指定します。
 */
export function BindLoading (...args) {
  DECORATOR_NAME = 'BindLoading';
  if (utils.isDecoratorCall(args)) {
    createAppDecorator(true)(...args);
  } else {
    return createAppDecorator(true, ...args);
  }
}

/**
 * その処理中ローディング変数をTRUEに設定することを宣言します
 *
 * 下記のパラメータを指定できます。
 * label: ローディング変数名を指定します。
 */
export function Loading (...args) {
  DECORATOR_NAME = 'Loading';
  if (utils.isDecoratorCall(args)) {
    createAppDecorator(false)(...args);
  } else {
    return createAppDecorator(false, ...args);
  }
}

function createAppDecorator (isBind, decoratorOptions = {}) {
  // 設定値のマージ
  decoratorOptions = DecoratorConfigs.marge(DECORATOR_NAME, decoratorOptions);

  // 実装時の整合性チェック
  if (isBind) {
    if (debug) {
      // BindLoading時
      utils.validateDecoratorOptions(
        DECORATOR_NAME, {
          label: { type: 'string', mustBe: ['not empty'] },
          resetBefore: { type: 'array', shouldBe: ['not empty'] },
          activeOn: { type: 'array', shouldBe: ['not empty'] }
        },
        decoratorOptions
      );
    }
  } else {
    if (debug) {
      // Loading時
      utils.validateDecoratorOptions(
        DECORATOR_NAME,
        {
          label: {
            type: 'string',
            mustBe: ['not empty']
          }
        },
        decoratorOptions
      );
    }
  }

  return createDecorator((options, key) => {
    // 実装時の整合性チェック
    if (isBind) {
      if (debug) {
        console.debug(`${DECORATOR_NAME}: @ ${key}`);

        if (key !== 'data') {
          throw new Error(`${DECORATOR_NAME}: can decorate only data().`);
        }
      }
    } else {
      if (debug) {
        console.debug(`${DECORATOR_NAME}: => @ ${key}`);

        if (key === 'data') {
          throw new Error(`${DECORATOR_NAME}: can not decorate data().`);
        }
      }
    }

    // オプション値取得
    const label = decoratorOptions.label || 'loading';
    const resetBefore = isBind ? decoratorOptions.resetBefore || [] : [];
    const activeOn = isBind ? decoratorOptions.activeOn || [] : [key];

    // created時の処理を追加
    const created = options.created || function () { };
    options.created = utils.injectBefore(created, function () {
      // 実装時の整合性チェック
      if (debug) {
        if (!(label in this)) {
          throw new Error(
            `${DECORATOR_NAME}: ${label} must be declared in data().`
          );
        }
        if (typeof this[label] !== 'boolean') {
          throw new Error(`${DECORATOR_NAME}: ${label} must be boolean value.`);
        }
      }

      // デコレータ管理用変数初期化
      this.$_sas = this.$_sas || {};
      this.$_sas.$_loading = this.$_sas.$_loading || {};
      if (!(label in this.$_sas.$_loading)) {
        this.$_sas.$_loading[label] = {
          sequence: 0,
          status: 0
        };
      }
    });

    // 非同期関数をラップして前後処理（フラグONとフラグOFF）を追加する
    utils.wrapMethods(
      options.methods,
      activeOn,
      function (args) {
        if (this.$_sas && this.$_sas.$_loading) {
          this.$_sas.$_loading[label].status++;
          const beforeStatus = this[label];
          const afterStatus = this.$_sas.$_loading[label].status > 0;
          this[label] = afterStatus;
          if (beforeStatus !== afterStatus) {
            this.$emit('update:' + label, afterStatus);
          }

          // before処理時のシーケンスをafterに引き渡す
          return this.$_sas.$_loading[label].sequence;
        } else {
          return null;
        }
      },
      function (args, result, sequence) {
        // before処理時と同一シーケンスの場合のみフラグ操作を行う。
        // $_sas が存在しない場合は既にComponentが破棄済みなので無視。
        if (this.$_sas && this.$_sas.$_loading) {
          if (sequence === this.$_sas.$_loading[label].sequence) {
            this.$_sas.$_loading[label].status--;

            const beforeStatus = this[label];
            const afterStatus = this.$_sas.$_loading[label].status > 0;
            this[label] = afterStatus;
            if (beforeStatus !== afterStatus) {
              this.$emit('update:' + label, afterStatus);
            }
          }
        }
      },
      true // async
    );

    // 関数をラップして事前処理（フラグOFF）を追加する
    utils.wrapMethods(options.methods, resetBefore, function () {
      if (this.$_sas && this.$_sas.$_loading) {
        // リセット時にシーケンスを切り替える
        this.$_sas.$_loading[label].sequence++;
        this.$_sas.$_loading[label].status = 0;

        const beforeStatus = this[label];
        const afterStatus = false;
        this[label] = afterStatus;
        if (beforeStatus !== afterStatus) {
          this.$emit('update:' + label, afterStatus);
        }
      }
    });

    // destroyed時の処理を追加
    const destroyed = options.destroyed || function () { };
    options.destroyed = utils.injectAfter(destroyed, function () {
      // デコレータ管理用変数破棄
      if (this.$_sas) {
        if (this.$_sas.$_loading) {
          delete this.$_sas.$_loading[label];
          if (Object.keys(this.$_sas.$_loading).length === 0) {
            delete this.$_sas.$_loading;
          }
        }
        if (Object.keys(this.$_sas).length === 0) {
          delete this.$_sas;
        }
      }
    });
  });
}



// WEBPACK FOOTER //
// ./src/core/decorators/BindLoading.js